import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Search, Check, MapPin, ChevronDown } from 'lucide-react';

// Complete list of countries
const ALL_COUNTRIES = [
  { name: 'Afghanistan', code: 'AF', phone: '+93', currency: 'AFN', symbol: '؋' },
  { name: 'Albania', code: 'AL', phone: '+355', currency: 'ALL', symbol: 'L' },
  { name: 'Algeria', code: 'DZ', phone: '+213', currency: 'DZD', symbol: 'د.ج' },
  { name: 'Argentina', code: 'AR', phone: '+54', currency: 'ARS', symbol: '$' },
  { name: 'Australia', code: 'AU', phone: '+61', currency: 'AUD', symbol: '$' },
  { name: 'Austria', code: 'AT', phone: '+43', currency: 'EUR', symbol: '€' },
  { name: 'Bangladesh', code: 'BD', phone: '+880', currency: 'BDT', symbol: '৳' },
  { name: 'Belgium', code: 'BE', phone: '+32', currency: 'EUR', symbol: '€' },
  { name: 'Botswana', code: 'BW', phone: '+267', currency: 'BWP', symbol: 'P' },
  { name: 'Brazil', code: 'BR', phone: '+55', currency: 'BRL', symbol: 'R$' },
  { name: 'Burundi', code: 'BI', phone: '+257', currency: 'BIF', symbol: 'FBu' },
  { name: 'Cameroon', code: 'CM', phone: '+237', currency: 'XAF', symbol: 'FCFA' },
  { name: 'Canada', code: 'CA', phone: '+1', currency: 'CAD', symbol: '$' },
  { name: 'Chile', code: 'CL', phone: '+56', currency: 'CLP', symbol: '$' },
  { name: 'China', code: 'CN', phone: '+86', currency: 'CNY', symbol: '¥' },
  { name: 'Colombia', code: 'CO', phone: '+57', currency: 'COP', symbol: '$' },
  { name: 'Congo (DRC)', code: 'CD', phone: '+243', currency: 'CDF', symbol: 'FC' },
  { name: 'Denmark', code: 'DK', phone: '+45', currency: 'DKK', symbol: 'kr' },
  { name: 'Egypt', code: 'EG', phone: '+20', currency: 'EGP', symbol: '£' },
  { name: 'Ethiopia', code: 'ET', phone: '+251', currency: 'ETB', symbol: 'Br' },
  { name: 'Finland', code: 'FI', phone: '+358', currency: 'EUR', symbol: '€' },
  { name: 'France', code: 'FR', phone: '+33', currency: 'EUR', symbol: '€' },
  { name: 'Germany', code: 'DE', phone: '+49', currency: 'EUR', symbol: '€' },
  { name: 'Ghana', code: 'GH', phone: '+233', currency: 'GHS', symbol: '₵' },
  { name: 'India', code: 'IN', phone: '+91', currency: 'INR', symbol: '₹' },
  { name: 'Indonesia', code: 'ID', phone: '+62', currency: 'IDR', symbol: 'Rp' },
  { name: 'Ireland', code: 'IE', phone: '+353', currency: 'EUR', symbol: '€' },
  { name: 'Israel', code: 'IL', phone: '+972', currency: 'ILS', symbol: '₪' },
  { name: 'Italy', code: 'IT', phone: '+39', currency: 'EUR', symbol: '€' },
  { name: 'Japan', code: 'JP', phone: '+81', currency: 'JPY', symbol: '¥' },
  { name: 'Kenya', code: 'KE', phone: '+254', currency: 'KES', symbol: 'KSh', pinned: true },
  { name: 'Malawi', code: 'MW', phone: '+265', currency: 'MWK', symbol: 'MK' },
  { name: 'Malaysia', code: 'MY', phone: '+60', currency: 'MYR', symbol: 'RM' },
  { name: 'Mexico', code: 'MX', phone: '+52', currency: 'MXN', symbol: '$' },
  { name: 'Morocco', code: 'MA', phone: '+212', currency: 'MAD', symbol: 'د.م.' },
  { name: 'Mozambique', code: 'MZ', phone: '+258', currency: 'MZN', symbol: 'MT' },
  { name: 'Namibia', code: 'NA', phone: '+264', currency: 'NAD', symbol: '$' },
  { name: 'Netherlands', code: 'NL', phone: '+31', currency: 'EUR', symbol: '€' },
  { name: 'New Zealand', code: 'NZ', phone: '+64', currency: 'NZD', symbol: '$' },
  { name: 'Nigeria', code: 'NG', phone: '+234', currency: 'NGN', symbol: '₦' },
  { name: 'Norway', code: 'NO', phone: '+47', currency: 'NOK', symbol: 'kr' },
  { name: 'Pakistan', code: 'PK', phone: '+92', currency: 'PKR', symbol: '₨' },
  { name: 'Philippines', code: 'PH', phone: '+63', currency: 'PHP', symbol: '₱' },
  { name: 'Poland', code: 'PL', phone: '+48', currency: 'PLN', symbol: 'zł' },
  { name: 'Portugal', code: 'PT', phone: '+351', currency: 'EUR', symbol: '€' },
  { name: 'Rwanda', code: 'RW', phone: '+250', currency: 'RWF', symbol: 'FRw' },
  { name: 'Saudi Arabia', code: 'SA', phone: '+966', currency: 'SAR', symbol: '﷼' },
  { name: 'Senegal', code: 'SN', phone: '+221', currency: 'XOF', symbol: 'CFA' },
  { name: 'Singapore', code: 'SG', phone: '+65', currency: 'SGD', symbol: '$' },
  { name: 'South Africa', code: 'ZA', phone: '+27', currency: 'ZAR', symbol: 'R' },
  { name: 'South Korea', code: 'KR', phone: '+82', currency: 'KRW', symbol: '₩' },
  { name: 'Spain', code: 'ES', phone: '+34', currency: 'EUR', symbol: '€' },
  { name: 'Sweden', code: 'SE', phone: '+46', currency: 'SEK', symbol: 'kr' },
  { name: 'Switzerland', code: 'CH', phone: '+41', currency: 'CHF', symbol: 'CHF' },
  { name: 'Tanzania', code: 'TZ', phone: '+255', currency: 'TZS', symbol: 'TSh', pinned: true },
  { name: 'Thailand', code: 'TH', phone: '+66', currency: 'THB', symbol: '฿' },
  { name: 'Turkey', code: 'TR', phone: '+90', currency: 'TRY', symbol: '₺' },
  { name: 'UAE', code: 'AE', phone: '+971', currency: 'AED', symbol: 'د.إ' },
  { name: 'Uganda', code: 'UG', phone: '+256', currency: 'UGX', symbol: 'USh', pinned: true },
  { name: 'Ukraine', code: 'UA', phone: '+380', currency: 'UAH', symbol: '₴' },
  { name: 'United Kingdom', code: 'GB', phone: '+44', currency: 'GBP', symbol: '£' },
  { name: 'United States', code: 'US', phone: '+1', currency: 'USD', symbol: '$' },
  { name: 'Vietnam', code: 'VN', phone: '+84', currency: 'VND', symbol: '₫' },
  { name: 'Zambia', code: 'ZM', phone: '+260', currency: 'ZMW', symbol: 'ZK' },
  { name: 'Zimbabwe', code: 'ZW', phone: '+263', currency: 'ZWL', symbol: '$' },
];

// Pinned countries at top (Tanzania, Uganda, Kenya)
const PINNED_COUNTRIES = ['Tanzania', 'Uganda', 'Kenya'];

const CountryPicker = ({ value, onChange, placeholder = "Select country" }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCountry = useMemo(() => {
    return ALL_COUNTRIES.find(c => c.name === value);
  }, [value]);

  const { pinnedCountries, otherCountries } = useMemo(() => {
    // Maintain the order: Tanzania, Uganda, Kenya
    const pinned = PINNED_COUNTRIES
      .map(name => ALL_COUNTRIES.find(c => c.name === name))
      .filter(Boolean);
    const others = ALL_COUNTRIES
      .filter(c => !PINNED_COUNTRIES.includes(c.name))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { pinnedCountries: pinned, otherCountries: others };
  }, []);

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) {
      return { pinned: pinnedCountries, others: otherCountries };
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = ALL_COUNTRIES.filter(c => 
      c.name.toLowerCase().includes(query) || 
      c.code.toLowerCase().includes(query)
    );
    
    // Maintain order: Tanzania, Uganda, Kenya for pinned results
    const pinnedFiltered = PINNED_COUNTRIES
      .map(name => filtered.find(c => c.name === name))
      .filter(Boolean);
    
    const othersFiltered = filtered
      .filter(c => !PINNED_COUNTRIES.includes(c.name))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return { 
      pinned: pinnedFiltered,
      others: othersFiltered
    };
  }, [searchQuery, pinnedCountries, otherCountries]);

  const handleSelect = (country) => {
    onChange(country.name);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between text-left font-normal"
        onClick={() => setOpen(true)}
        data-testid="country-picker-trigger"
      >
        {selectedCountry ? (
          <div className="flex items-center gap-2">
            <span className="text-lg">{getFlagEmoji(selectedCountry.code)}</span>
            <span>{selectedCountry.name}</span>
            <span className="text-muted-foreground text-sm">({selectedCountry.currency})</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className="h-4 w-4 opacity-50 ml-auto flex-shrink-0" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="country-picker-modal">
          <DialogHeader>
            <DialogTitle>Select Country</DialogTitle>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
              data-testid="country-search-input"
            />
          </div>

          {/* Country List */}
          <ScrollArea className="h-[300px] pr-4">
            {/* Pinned Countries */}
            {filteredCountries.pinned.length > 0 && (
              <div className="mb-2">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 bg-muted/50 rounded mb-1">
                  Featured
                </div>
                {filteredCountries.pinned.map((country) => (
                  <CountryItem
                    key={country.code}
                    country={country}
                    isSelected={value === country.name}
                    onSelect={() => handleSelect(country)}
                  />
                ))}
              </div>
            )}

            {/* Divider */}
            {filteredCountries.pinned.length > 0 && filteredCountries.others.length > 0 && (
              <div className="border-t my-2" />
            )}

            {/* All Other Countries */}
            {filteredCountries.others.length > 0 && (
              <div>
                {!searchQuery && (
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 bg-muted/50 rounded mb-1">
                    All Countries
                  </div>
                )}
                {filteredCountries.others.map((country) => (
                  <CountryItem
                    key={country.code}
                    country={country}
                    isSelected={value === country.name}
                    onSelect={() => handleSelect(country)}
                  />
                ))}
              </div>
            )}

            {/* No Results */}
            {filteredCountries.pinned.length === 0 && filteredCountries.others.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No countries found</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CountryItem = ({ country, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
      isSelected 
        ? 'bg-primary/10 text-primary' 
        : 'hover:bg-muted'
    }`}
    data-testid={`country-option-${country.code}`}
  >
    <span className="text-xl">{getFlagEmoji(country.code)}</span>
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate">{country.name}</div>
      <div className="text-xs text-muted-foreground">
        {country.currency} ({country.symbol}) • {country.phone}
      </div>
    </div>
    {isSelected && <Check className="h-4 w-4 flex-shrink-0" />}
  </button>
);

// Helper function to convert country code to flag emoji
function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default CountryPicker;
export { ALL_COUNTRIES, PINNED_COUNTRIES };
